import { ref, computed, getCurrentScope, onScopeDispose } from 'vue'
import { authProvider } from '@/services/currentAuthProvider'
import { initApiService } from '@/services/init'
import { getAdminPortalUrl, getAppMode } from '@/utils/domain'
import { getApiErrorCode, getAxiosStatus, logUnexpectedFailure } from '@/utils/errorHandling'

interface ImpersonationState {
  isImpersonating: boolean
  targetEmail: string | null
  adminUserId: string | null
  adminEmail: string | null
  impersonationStartedAt: Date | null
}

const DEFAULT_STATE: ImpersonationState = {
  isImpersonating: false,
  targetEmail: null,
  adminUserId: null,
  adminEmail: null,
  impersonationStartedAt: null,
}

/**
 * How long an impersonated session lasts. The backend is what enforces it —
 * `AdminImpersonationService.IMPERSONATION_SESSION_DURATION` — and this is the
 * same hour written a second time, so the banner counts down to the moment the
 * API starts refusing. The two cannot generate one another; move them together.
 */
export const IMPERSONATION_SESSION_DURATION_MS = 60 * 60 * 1000

/** `code` on the 401 the backend answers an expired impersonated session with (`FirebaseAuthenticationFilter`). */
export const IMPERSONATION_EXPIRED_CODE = 'impersonation-expired'

const MS_PER_MINUTE = 60 * 1000
const COUNTDOWN_TICK_MS = 1000

/**
 * Reads the `impersonationStartedAt` claim, which the backend writes as an
 * ISO-8601 instant. Anything else is no start at all: the countdown is then
 * left out, and the backend — which counts an unreadable start as expired —
 * is what ends the session.
 */
export function parseImpersonationStartedAt(claim: unknown): Date | null {
  if (typeof claim !== 'string') return null
  const startedAt = new Date(claim)
  return Number.isNaN(startedAt.getTime()) ? null : startedAt
}

/** Whether `err` is the backend refusing an impersonated session whose hour is up (#4678). */
function isImpersonationExpired(err: unknown): boolean {
  return getAxiosStatus(err) === 401 && getApiErrorCode(err) === IMPERSONATION_EXPIRED_CODE
}

/**
 * Puts the end of the session on record as an `IMPERSONATION_ENDED` entry (#4680).
 * It has to run before the sign-out: the impersonated session's token is the
 * only one that can say which session is ending.
 *
 * Never rejects. Recording the end must not keep anyone inside an impersonated
 * session, and a session refused as expired already had its end recorded by
 * the backend, so that refusal is not a failure worth logging.
 */
async function recordImpersonationEnd(): Promise<void> {
  try {
    await initApiService().security.endImpersonation()
  } catch (err) {
    if (!isImpersonationExpired(err)) {
      logUnexpectedFailure('Failed to record the end of impersonation:', err)
    }
  }
}

/** One sign-out, however many callers ask — the timer and several refused requests land together. */
let sessionEnd: Promise<void> | null = null

/**
 * Record the end of the impersonated session, sign out of it and return to this
 * environment's admin portal. Every end goes through here — the banner, the
 * countdown and a refused request alike. Rejects when the sign-out fails,
 * leaving the session where it was.
 */
function endImpersonationSession(): Promise<void> {
  sessionEnd ??= recordImpersonationEnd()
    .then(() => authProvider.signOut())
    .then(() => {
      window.location.href = getAdminPortalUrl()
    })
    .finally(() => {
      sessionEnd = null
    })
  return sessionEnd
}

/**
 * Ends the session when `err` is the backend saying the impersonation is over.
 * Any other failure — an ordinary 401 included — is left to its own handling.
 *
 * A module function rather than part of the composable so the axios interceptor
 * can call it: the refusal is true of the whole session, whatever screen the
 * request came from.
 *
 * @returns whether the failure was an expired impersonation.
 */
export function endImpersonationIfExpired(err: unknown): boolean {
  if (!isImpersonationExpired(err)) return false
  endImpersonationSession().catch((signOutError) => {
    logUnexpectedFailure('Failed to end expired impersonation:', signOutError)
  })
  return true
}

/**
 * Composable for managing impersonation state in app.scrivio.nl.
 *
 * Detects impersonation by reading Firebase ID token custom claims:
 * - impersonatedBy: admin user ID who initiated impersonation
 * - impersonatedByEmail: admin email address
 * - impersonationStartedAt: ISO-8601 instant at which impersonation started
 *
 * The target user email comes from the signed-in session since
 * the user is signed in as the impersonated user via custom token.
 */
export function useImpersonation() {
  const state = ref<ImpersonationState>({ ...DEFAULT_STATE })
  const isEnding = ref(false)
  const error = ref<string | null>(null)
  const isInitialized = ref(false)
  const now = ref(Date.now())
  let countdownTimer: ReturnType<typeof setInterval> | null = null

  const isImpersonating = computed(() => state.value.isImpersonating)
  const targetEmail = computed(() => state.value.targetEmail)
  const adminEmail = computed(() => state.value.adminEmail)

  const expiresAt = computed<Date | null>(() => {
    const startedAt = state.value.impersonationStartedAt
    return startedAt ? new Date(startedAt.getTime() + IMPERSONATION_SESSION_DURATION_MS) : null
  })
  // Rounded up, so the banner reads "1 min" through the final minute rather than "0".
  const remainingMinutes = computed<number | null>(() => {
    if (!expiresAt.value) return null
    return Math.max(0, Math.ceil((expiresAt.value.getTime() - now.value) / MS_PER_MINUTE))
  })

  function stopCountdown() {
    if (countdownTimer === null) return
    clearInterval(countdownTimer)
    countdownTimer = null
  }

  function tick() {
    now.value = Date.now()
    if (expiresAt.value && now.value >= expiresAt.value.getTime()) {
      stopCountdown()
      void endImpersonation()
    }
  }

  /** Runs the countdown for as long as there is an expiry to count down to. */
  function syncCountdown() {
    if (!expiresAt.value) {
      stopCountdown()
      return
    }
    countdownTimer ??= setInterval(tick, COUNTDOWN_TICK_MS)
    tick()
  }

  function setState(next: ImpersonationState) {
    state.value = next
    syncCountdown()
  }

  /**
   * Check Firebase ID token for impersonation claims.
   */
  async function checkImpersonationClaims(): Promise<void> {
    // Only process impersonation on app.scrivio.nl (behandelaar mode)
    if (getAppMode() !== 'behandelaar') {
      return
    }

    const session = authProvider.currentSession()
    if (!session) {
      clearImpersonation()
      return
    }

    try {
      const claims = (await authProvider.getTokenClaims()) ?? {}

      const impersonatedBy = claims.impersonatedBy as string | undefined
      const impersonatedByEmail = claims.impersonatedByEmail as string | undefined

      if (impersonatedBy) {
        setState({
          isImpersonating: true,
          targetEmail: session.email,
          adminUserId: impersonatedBy,
          adminEmail: impersonatedByEmail ?? null,
          impersonationStartedAt: parseImpersonationStartedAt(claims.impersonationStartedAt),
        })
      } else {
        clearImpersonation()
      }
    } catch (err) {
      logUnexpectedFailure('Failed to get ID token result:', err)
      clearImpersonation()
    }
  }

  /**
   * Initialize impersonation state by checking Firebase claims.
   * Sets up auth state listener to update state on changes.
   */
  function initialize() {
    if (isInitialized.value) {
      return
    }
    isInitialized.value = true

    // Check claims immediately if user is already signed in
    checkImpersonationClaims()

    // Watch for auth state changes
    authProvider.onAuthStateChanged(() => {
      checkImpersonationClaims()
    })
  }

  /**
   * Clear impersonation state locally.
   */
  function clearImpersonation() {
    setState({ ...DEFAULT_STATE })
  }

  /**
   * End impersonation session: record the end, sign out and return to the admin portal.
   */
  async function endImpersonation() {
    if (!state.value.isImpersonating) return

    isEnding.value = true
    error.value = null

    try {
      await endImpersonationSession()
      clearImpersonation()
    } catch (err) {
      error.value = 'endSessionError'
      logUnexpectedFailure('Failed to end impersonation:', err)
    } finally {
      isEnding.value = false
    }
  }

  if (getCurrentScope()) {
    onScopeDispose(stopCountdown)
  }

  // Initialize immediately when composable is used
  initialize()

  return {
    // State
    isImpersonating,
    targetEmail,
    adminEmail,
    expiresAt,
    remainingMinutes,
    isEnding,
    error,

    // Actions
    endImpersonation,
    clearImpersonation,
    initialize,
  }
}
